import { Injectable } from '@angular/core';
import { DefaultDataService, HttpUrlGenerator } from '@ngrx/data';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ApiResponse } from '@core/models/api-response.model';
import { Pagination } from '@core/models/pagination.model';
import { BlacklistTemplate, BlacklistTemplateSetting } from '@core/models/blacklist-template.model';
import { SweetAlertIcon } from 'sweetalert2';

// export type SubCategoriesByCurrency = Record<number, number[]>;
export type SubCategoriesByCurrency = Record<number, Record<number, number[]>>;

export interface SaveBlacklistTemplatePayload {
  name: string;
  remarks: string;
  status: number;
  sub_categories: SubCategoriesByCurrency; // better than `object`
}

type RowsResponse<T> = ApiResponse & {
  data: {
    rows: T;
    paginations?: Pagination;
  };
  message?: string[] | string;
};

@Injectable()
export class BlacklistTemplateService extends DefaultDataService<BlacklistTemplate> {
  pagination: Pagination | null = null;

  // BehaviorSubject so UI can bind + we can clear safely
  readonly messages$ = new Subject<any[] | null>();
  readonly icon$ = new Subject<SweetAlertIcon>();

  constructor(http: HttpClient, httpUrlGenerator: HttpUrlGenerator) {
    super('BlacklistTemplate', http, httpUrlGenerator, { root: '' });
  }

  // Clear popup messages (call when opening dialog / after user confirms swal)
  clearMessages(): void {
    this.messages$.next(null);
    this.icon$.next('info');
  }

  /** List API */
  getList(queryString: string): Observable<BlacklistTemplate[]> {
    return this.http.get<RowsResponse<{ rows: BlacklistTemplate[] }>>(`/blacklist${queryString}`).pipe(
      tap(res => this.paginationInit(res)),
      map(res => res.data.rows)
    );
  }

  /** Settings API: create mode */
  getGameProvider(): Observable<BlacklistTemplateSetting> {
    return this.http.get<RowsResponse<BlacklistTemplateSetting>>(`/blacklist/gameprovider`).pipe(
      map(res => res.data.rows)
    );
  }

  /** Settings API: edit/duplicate mode */
  getGameProviderById(id: number): Observable<BlacklistTemplateSetting> {
    return this.http.get<RowsResponse<BlacklistTemplateSetting>>(`/blacklist/gameprovider/${id}`).pipe(
      map(res => res.data.rows)
    );
  }

  getGameProviderByPromotion(params: {
    promotion_id: number | null;
    blacklist_id: number | null;
    currency_ids: number[];
    category_codes: string[];
    game_provider_ids: number[];
  }): Observable<BlacklistTemplateSetting> {
    return this.http.post<RowsResponse<BlacklistTemplateSetting>>('/blacklist/promotion', params).pipe(
      map(res => res.data.rows)
    );
  }

  getGameProviderByRebate(params: {
    rebate_setting_id: number | null;
    blacklist_id: number | null;
    currency_ids: number[];
    category_codes: string[];
    game_provider_ids: number[];
  }): Observable<BlacklistTemplateSetting> {
    return this.http.post<RowsResponse<BlacklistTemplateSetting>>('/blacklist/rebate', params).pipe(
      map(res => res.data.rows)
    );
  }

  createTemplate(payload: SaveBlacklistTemplatePayload): Observable<ApiResponse> {
    return this.http.post<RowsResponse<any>>(`/blacklist`, payload).pipe(
      tap(res => this.setMessages(res?.message)),
      catchError(err => {
        const msg = err?.error?.message ?? err?.message ?? ['Failed to create.'];
        this.setMessages(msg, 'error');
        return throwError(() => err);
      })
    );
  }

  updateTemplate(id: number, payload: SaveBlacklistTemplatePayload): Observable<ApiResponse> {
    return this.http.put<RowsResponse<any>>(`/blacklist/${id}`, payload).pipe(
      tap(res => this.setMessages(res?.message)),
      catchError(err => {
        const msg = err?.error?.message ?? err?.message ?? ['Failed to update.'];
        this.setMessages(msg, 'error');
        return throwError(() => err);
      })
    );
  }

  // -----------------------
  // helpers
  // -----------------------
  private setMessages(message?: string[] | string, icon: SweetAlertIcon = 'info'): void {
    this.icon$.next(icon);

    // normalize
    let msgs: any[] | null = null;

    if (!message) {
      msgs = ['Success'];
    } else if (Array.isArray(message)) {
      msgs = message.length > 0 ? message : null;   // ✅ empty -> null
    } else {
      msgs = [message];
    }

    // only emit when it has something
    if (msgs && msgs.length > 0) {
      this.messages$.next(msgs);
    } else {
      this.messages$.next(null);
    }
  }

  private paginationInit(res: any): void {
    const p = res?.data?.paginations;
    this.pagination = p ?? null;
  }
}
